import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Receipt, Download } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { downloadBillPdf } from "../../lib/billDownload";
import { formatINR, formatDate } from "../../lib/utils.fmt";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import LoginRequired from "./LoginRequired";
import { toast } from "sonner";

function openBillPdf(invoiceId, label) {
  downloadBillPdf({
    pdfPath: `/api/customer/bills/${invoiceId}/pdf`,
    linkPath: `/api/customer/bills/${invoiceId}/pdf-link`,
    filename: `invoice_${label}.pdf`,
  }).catch(() => toast.error("Couldn't download bill — try again"));
}

export default function MyBills() {
  const { user } = useAuth();
  const [bills, setBills] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/api/customer/bills").then(({ data }) => setBills(data.bills || [])).catch(() => setBills([]));
  }, [user]);

  if (!user) return <LoginRequired title="Sign in to view your bills" description="See your weekly/monthly invoices and download them as PDF." />;
  if (bills === null) return <LoadingState fullHeight testId="my-bills-loading" />;

  return (
    <div className="page-enter" data-testid="my-bills-page">
      <header className="sticky top-0 z-20 bg-brand-cream/95 backdrop-blur px-5 pt-4 pb-3 flex items-center gap-2">
        <Link to="/app/profile" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <h1 className="font-heading text-xl font-extrabold tracking-tight">My Bills</h1>
      </header>

      <main className="px-5 pb-10 space-y-3">
        {bills.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title="No bills yet"
            description="Weekly or monthly bills will show up here once your store generates one."
            testId="my-bills-empty"
          />
        ) : (
          bills.map((b) => (
            <div key={b.id} className="bg-white rounded-3xl border border-neutral-200 p-4 flex items-center gap-3" data-testid={`bill-${b.id}`}>
              <div className="w-11 h-11 rounded-2xl bg-brand-sky/10 flex items-center justify-center shrink-0">
                <Receipt className="w-5 h-5 text-brand-sky" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{formatDate(b.period_start)} – {formatDate(b.period_end)}</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Generated {formatDate(b.generated_at)}
                  {b.delivery_status === "sent" && <span className="text-brand-leaf font-bold"> · Notified</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading font-extrabold">{formatINR(b.total)}</div>
                <button onClick={() => openBillPdf(b.id, `${b.period_start}_${b.period_end}`)}
                        className="mt-1 text-xs font-bold text-brand-sky inline-flex items-center gap-1"
                        data-testid={`bill-download-${b.id}`}>
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}
